import React from 'react';
import Icon from '../../../components/AppIcon';

const AnalogySection = () => {
  const analogies = [
    {
      title: "Renting vs. Owning Your Home",
      proprietary: {
        icon: "Home",
        title: "Proprietary AI = Renting Forever",
        points: [
          "Monthly payments that never end",
          "No equity building or ownership",
          "Landlord controls your modifications",
          "Rent increases at their discretion",
          "No asset to show for decades of payments"
        ],
        color: "text-red-500"
      },
      opensource: {
        icon: "Key",
        title: "Open-Source AI = Owning Your Castle",
        points: [
          "One-time investment builds lasting value",
          "Complete control over modifications",
          "No monthly rent to external parties",
          "Asset appreciates with your improvements",
          "Freedom to customize without permission"
        ],
        color: "text-green-500"
      }
    },
    {
      title: "Building vs. Buying Your Business Tools",
      proprietary: {
        icon: "ShoppingCart",
        title: "Proprietary = Expensive Dependency",
        points: [
          "Vendor controls your pricing destiny",
          "Limited customization options",
          "Data locked in their ecosystem",
          "Innovation at their pace, not yours",
          "Exit costs make switching painful"
        ],
        color: "text-red-500"
      },
      opensource: {
        icon: "Wrench",
        title: "Open-Source = Strategic Independence",
        points: [
          "You control costs and capabilities",
          "Unlimited customization potential",
          "Your data stays under your control",
          "Innovation moves at your speed",
          "No vendor lock-in or exit fees"
        ],
        color: "text-green-500"
      }
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-brand-primary mb-6">
            Stop Renting Your Competitive Advantage
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            The choice between proprietary and open-source AI is like choosing between renting and owning. 
            One builds equity, the other builds dependency.
          </p>
        </div>

        <div className="space-y-20">
          {analogies?.map((analogy, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-executive p-8 lg:p-12">
              <h3 className="text-2xl font-bold text-brand-primary mb-12 text-center">
                {analogy?.title}
              </h3>
              
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Proprietary Side */}
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <Icon name="X" size={16} className="text-red-500" />
                  </div>
                  
                  <div className="border-2 border-red-200 rounded-xl p-6 bg-red-50/50">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                        <Icon name={analogy?.proprietary?.icon} size={24} className={analogy?.proprietary?.color} />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">
                        {analogy?.proprietary?.title}
                      </h4>
                    </div>
                    
                    <ul className="space-y-3">
                      {analogy?.proprietary?.points?.map((point, pointIndex) => (
                        <li key={pointIndex} className="flex items-start">
                          <Icon name="Minus" size={16} className="text-red-500 mt-1 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Open Source Side */}
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={16} className="text-green-500" />
                  </div>
                  
                  <div className="border-2 border-green-200 rounded-xl p-6 bg-green-50/50">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                        <Icon name={analogy?.opensource?.icon} size={24} className={analogy?.opensource?.color} />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">
                        {analogy?.opensource?.title}
                      </h4>
                    </div>
                    
                    <ul className="space-y-3">
                      {analogy?.opensource?.points?.map((point, pointIndex) => (
                        <li key={pointIndex} className="flex items-start">
                          <Icon name="Plus" size={16} className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-brand-primary rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Own Your AI Future?
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Calculate exactly how much you could save by switching from proprietary to open-source AI solutions.
            </p>
            <button className="bg-brand-accent hover:bg-brand-accent/90 text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors duration-200">
              Start Your Savings Calculator
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnalogySection;